import React, { useState, useEffect } from 'react';
import {
  Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, FormControl, FormControlLabel, IconButton,
  InputLabel, MenuItem, Paper, Radio, RadioGroup, Select, Snackbar, Switch, TextField, Typography,
  Alert, Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon, Save as SaveIcon } from '@mui/icons-material';
import {useNavigate} from "react-router-dom";

const ProductOptionsManager = ({ api, productId }) => {
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [newOption, setNewOption] = useState({ name: '', type: 'single_choice', values: [''], default_value: '', required: false });
  const [optionDialogOpen, setOptionDialogOpen] = useState(false);
  const [editingOption, setEditingOption] = useState(null);
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('import'); // 'import' | 'apply'
  const [pendingOption, setPendingOption] = useState(null); // option being added to a product
  const [products, setProducts] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedOptionKey, setSelectedOptionKey] = useState('');
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const navigate = useNavigate();
  const StoreId = localStorage.getItem('ecwid_store_id');

  useEffect(() => { if (api) fetchStoredOptions(); }, [api]);
  useEffect(() => { if (api) fetchProducts(); }, [api]);
  useEffect(() => { if (api && productId) fetchProductOptions(); }, [api, productId]);

  const fetchStoredOptions = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/options');
      setOptions(response.data);
    } catch (error) {
      console.error('Error fetching stored options:', error);
      setNotification({ open: true, message: 'Failed to load stored options', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/products');
      const loadedProducts = response.data.items || response.data || [];
      setProducts(loadedProducts);
    } catch (error) {
      console.error('Error fetching products:', error);
      setNotification({ open: true, message: 'Failed to load products', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fetchProductOptions = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/api/products/${productId}/options`);
      setSelectedOptions(response.data);
    } catch (error) {
      console.error('Error fetching product options:', error);
      setNotification({ open: true, message: 'Failed to load product options', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const saveProductOptions = async () => {
    setLoading(true);
    try {
      const response = await api.put(`/api/products/${productId}/options`, { options: selectedOptions });
      if (response.status === 200) {
        setNotification({ open: true, message: 'Product options saved successfully', severity: 'success' });
      } else {
        throw new Error('Failed to save product options');
      }
    } catch (error) {
      console.error('Error saving product options:', error);
      setNotification({ open: true, message: 'Failed to save product options', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleAddOption = async () => {
    const filteredValues = newOption.values.filter(val => val.trim() !== '');
    if (newOption.name.trim() === '' || filteredValues.length === 0) {
      setNotification({ open: true, message: 'Option name and at least one value are required', severity: 'error' });
      return;
    }
    try {
      const payload = { ...newOption, values: filteredValues };
      const response = await api.post('/api/options', payload);
      setOptions((prev) => [...prev, response.data]);
      setNewOption({ name: '', type: 'single_choice', values: [''], default_value: '', required: false });
      setOptionDialogOpen(false);
      setNotification({ open: true, message: 'Option added successfully', severity: 'success' });
    } catch (error) {
      console.error('Error adding option:', error);
      setNotification({ open: true, message: 'Failed to add option', severity: 'error' });
    }
  };

  const handleEditOption = (option) => {
    setEditingOption(option);
    let valuesArray = option.values;
    if (typeof valuesArray === 'string') {
      try {
        valuesArray = JSON.parse(option.values);
      } catch (e) {
        valuesArray = option.values.split(',').map(v => v.trim());
      }
    }
    setNewOption({ ...option, values: valuesArray });
    setOptionDialogOpen(true);
  };

  const handleUpdateOption = async () => {
    const filteredValues = newOption.values.filter(val => val.trim() !== '');
    if (newOption.name.trim() === '' || filteredValues.length === 0) {
      setNotification({ open: true, message: 'Option name and at least one value are required', severity: 'error' });
      return;
    }
    try {
      const payload = { ...newOption, values: filteredValues };
      const response = await api.put(`/api/options/${editingOption.id}`, payload);
      setOptions((prev) => prev.map((opt) => (opt.id === editingOption.id ? response.data : opt)));
      setEditingOption(null);
      setOptionDialogOpen(false);
      setNotification({ open: true, message: 'Option updated successfully', severity: 'success' });
    } catch (error) {
      console.error('Error updating option:', error);
      setNotification({ open: true, message: 'Failed to update option', severity: 'error' });
    }
  };

  const handleRemoveOption = async (id) => {
    try {
      await api.delete(`/api/options/${id}`);
      setOptions((prev) => prev.filter((opt) => opt.id !== id));
      setNotification({ open: true, message: 'Option removed successfully', severity: 'success' });
    } catch (error) {
      console.error('Error removing option:', error);
      setNotification({ open: true, message: 'Failed to remove option', severity: 'error' });
    }
  };

  const resetProductDialog = () => {
    setSelectedProductId('');
    setSelectedProduct(null);
    setSelectedOptionKey('');
  };

  // Toolbar button — imports an option FROM a product INTO the library
  const handleImportProductOptions = () => {
    resetProductDialog();
    setDialogMode('import');
    setProductDialogOpen(true);
  };

  // Row button — applies a stored option TO a product
  const handleAddToProduct = (option) => {
    resetProductDialog();
    setPendingOption(option);
    setDialogMode('apply');
    setProductDialogOpen(true);
  };

  // Apply the pending stored option to the selected product
  const handleApplyOptionToProduct = async () => {
    if (!selectedProductId) {
      setNotification({ open: true, message: 'Please select a product first', severity: 'error' });
      return;
    }
    try {
      await api.put(`/api/products/${selectedProductId}/options`, { option: pendingOption });
      setNotification({ open: true, message: `Option "${pendingOption.name}" added to product successfully`, severity: 'success' });
      setProductDialogOpen(false);
      setPendingOption(null);
      resetProductDialog();
    } catch (error) {
      const msg = error.response?.data?.error || 'Failed to add option to product';
      setNotification({ open: true, message: msg, severity: 'error' });
    }
  };

  const getProductOptionKey = (option) => {
    if (option.id !== undefined && option.id !== null) return String(option.id);
    return option.name || `${option.type}-${Math.random().toString(36).slice(2, 7)}`;
  };

  const handleProductSelect = (event) => {
    const id = event.target.value;
    setSelectedProductId(id);
    const product = products.find((p) => String(p.id) === String(id));
    setSelectedProduct(product || null);
    setSelectedOptionKey('');
  };

  const handleProductOptionSelect = (optionKey) => {
    setSelectedOptionKey(optionKey);
  };

  const handleSaveSelectedProductOptions = async () => {
    if (!selectedProduct) {
      setNotification({ open: true, message: 'Please select a product first', severity: 'error' });
      return;
    }
    if (!selectedOptionKey) {
      setNotification({ open: true, message: 'Select a product option to save', severity: 'error' });
      return;
    }
    const option = (selectedProduct.options || []).find(
      (o) => getProductOptionKey(o) === selectedOptionKey
    );
    try {
      await api.post('/api/options', {
        name: option.name,
        type: option.type === 'SELECT' ? 'single_choice' : (option.type || 'single_choice'),
        values: (option.choices || []).map((choice) => choice.text),
        default_value: option.default_value || '',
        required: option.required || false,
        store_id: StoreId,
      });
      setNotification({ open: true, message: 'Product options saved to store successfully', severity: 'success' });
      setProductDialogOpen(false);
      resetProductDialog();
      fetchStoredOptions();
    } catch (error) {
      console.error('Error saving product options to store:', error);
      setNotification({ open: true, message: 'Failed to save product options to store', severity: 'error' });
    }
  };

  const handleValueChange = (index, value) => {
    const newValues = [...newOption.values];
    newValues[index] = value;
    setNewOption({ ...newOption, values: newValues });
  };

  const handleAddNewValue = () => {
    setNewOption({ ...newOption, values: [...newOption.values, ''] });
  };

  const handleRemoveValue = (index) => {
    const newValues = newOption.values.filter((_, idx) => idx !== index);
    setNewOption({ ...newOption, values: newValues });
  };

  const getOptionValues = (option) => {
    if (typeof option.values === 'string') {
      try { return JSON.parse(option.values); } catch { return option.values.split(',').map(v => v.trim()); }
    }
    return option.values || [];
  };

  return (
      <Box sx={{ width: '100%' }}>
        <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5" component="h1">Product Options Manager</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => {
                setNewOption({ name: '', type: 'single_choice', values: [''], default_value: '', required: false });
                setEditingOption(null);
                setOptionDialogOpen(true);
              }}>
                Add New Option
              </Button>
              <Button variant="outlined" onClick={handleImportProductOptions}>
                Import Product Options
              </Button>
            </Box>
          </Box>

          <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Bundle Options</Typography>
          {options.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8, border: '1px dashed #CBD5E1', borderRadius: 2, mb: 3 }}>
              <SaveIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 1.5 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>No options yet</Typography>
              <Typography variant="body2" color="text.disabled" sx={{ mb: 2 }}>
                Add your first option or import from a product.
              </Typography>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOptionDialogOpen(true)}>
                Add Option
              </Button>
            </Box>
          ) : (
          <TableContainer component={Paper} sx={{ mb: 3 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Values</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {options.map((option) => (
                    <TableRow key={option.id}>
                      <TableCell>{option.name}</TableCell>
                      <TableCell>{option.type}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {getOptionValues(option).map((value, i) => (
                            <Chip key={i} label={value} size="small" />
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <IconButton onClick={() => handleEditOption(option)}>
                          <EditIcon />
                        </IconButton>
                        <IconButton onClick={() => handleRemoveOption(option.id)} color="error">
                          <DeleteIcon />
                        </IconButton>
                        <Button onClick={() => handleAddToProduct(option)}>Add to Product</Button>
                      </TableCell>
                    </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          )}
        </Paper>

        {/* Add/Edit Option Dialog */}
        <Dialog open={optionDialogOpen} onClose={() => setOptionDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{editingOption ? 'Edit Option' : 'Add New Option'}</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                  label="Option Name"
                  value={newOption.name}
                  onChange={(e) => setNewOption({ ...newOption, name: e.target.value })}
                  fullWidth
              />
              <FormControl fullWidth>
                <InputLabel>Option Type</InputLabel>
                <Select
                    value={newOption.type}
                    onChange={(e) => setNewOption({ ...newOption, type: e.target.value })}
                    label="Option Type"
                >
                  <MenuItem value="single_choice">Single Choice</MenuItem>
                  <MenuItem value="multi_choice">Multi Choice</MenuItem>
                  <MenuItem value="dropdown">Dropdown</MenuItem>
                  <MenuItem value="checkbox">Checkbox</MenuItem>
                </Select>
              </FormControl>
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>Option Values</Typography>
                <Box sx={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #ccc', borderRadius: 1, p: 1 }}>
                  {newOption.values.map((val, idx) => (
                      <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <TextField
                            label={`Value ${idx + 1}`}
                            value={val}
                            onChange={(e) => handleValueChange(idx, e.target.value)}
                            fullWidth
                        />
                        <IconButton onClick={() => handleRemoveValue(idx)} color="error">
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                  ))}
                </Box>
                <Button onClick={handleAddNewValue} variant="outlined" startIcon={<AddIcon />} sx={{ mt: 1 }}>
                  Add New Value
                </Button>
              </Box>
              <TextField
                  label="Default Value"
                  value={newOption.default_value}
                  onChange={(e) => setNewOption({ ...newOption, default_value: e.target.value })}
                  fullWidth
              />
              <FormControlLabel
                  control={<Switch checked={newOption.required} onChange={(e) => setNewOption({ ...newOption, required: e.target.checked })} />}
                  label="Required"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOptionDialogOpen(false)}>Cancel</Button>
            <Button onClick={editingOption ? handleUpdateOption : handleAddOption} variant="contained" color="primary">
              {editingOption ? 'Update Option' : 'Add Option'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Product Dialog — "apply" mode: add stored option to a product */}
        {/* "import" mode: import option from a product into the library */}
        <Dialog open={productDialogOpen} onClose={() => setProductDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            {dialogMode === 'apply' ? 'Add Option to Product' : 'Import Product Options'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>

              {/* Apply mode: show the option being added */}
              {dialogMode === 'apply' && pendingOption && (
                <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'action.hover' }}>
                  <Typography variant="subtitle2" gutterBottom>Option to add:</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>{pendingOption.name}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Type: {pendingOption.type}</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {getOptionValues(pendingOption).map((v, i) => <Chip key={i} label={v} size="small" />)}
                  </Box>
                </Box>
              )}

              <FormControl fullWidth>
                <InputLabel id="product-select-label">Select Product</InputLabel>
                <Select
                  labelId="product-select-label"
                  value={selectedProductId}
                  label="Select Product"
                  onChange={handleProductSelect}
                >
                  {products.map((product) => (
                    <MenuItem key={product.id} value={product.id}>
                      {product.name || product.title || `Product ${product.id}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Import mode only: pick which option from the selected product to import */}
              {dialogMode === 'import' && (
                selectedProduct ? (
                  <Box>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>Product Options</Typography>
                    {selectedProduct.options && selectedProduct.options.length > 0 ? (
                      <RadioGroup
                        value={selectedOptionKey}
                        onChange={(e) => handleProductOptionSelect(e.target.value)}
                      >
                        {selectedProduct.options.map((option) => {
                          const optionKey = getProductOptionKey(option);
                          return (
                            <FormControlLabel
                              key={optionKey}
                              value={optionKey}
                              control={<Radio />}
                              label={`${option.name} ${option.type ? `(${option.type})` : ''}`}
                            />
                          );
                        })}
                      </RadioGroup>
                    ) : (
                      <Typography color="text.secondary">This product does not have any selectable options.</Typography>
                    )}
                  </Box>
                ) : (
                  <Typography color="text.secondary">Choose a product to view its options.</Typography>
                )
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setProductDialogOpen(false)}>Cancel</Button>
            {dialogMode === 'apply' ? (
              <Button
                onClick={handleApplyOptionToProduct}
                variant="contained"
                disabled={!selectedProductId}
              >
                Add to Product
              </Button>
            ) : (
              <Button
                onClick={handleSaveSelectedProductOptions}
                variant="contained"
                disabled={!selectedProduct || !selectedOptionKey}
              >
                Save Options to Store
              </Button>
            )}
          </DialogActions>
        </Dialog>

        <Snackbar open={notification.open} autoHideDuration={6000} onClose={() => setNotification({ ...notification, open: false })}>
          <Alert onClose={() => setNotification({ ...notification, open: false })} severity={notification.severity} sx={{ width: '100%' }}>
            {notification.message}
          </Alert>
        </Snackbar>
      </Box>
  );
};

export default ProductOptionsManager;
